import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { AnnualReportBusinessModel } from '@modeso/aumico-lib-annualreport-fe';
import {
    AumicoCoreProvider,
    ModalComponent,
    ModalData,
    ModalTitleIcons,
    NotificationType,
    Translation,
} from '@modeso/aumico-lib-core-fe';
import {
    IAccount,
    IAccountMappingTemplate,
    IAccountMappingTemplateGroup,
    IAccountMappingTemplateGroupItemFLI,
    IFinStatements,
    IFinancialStatementInterpretation,
    ItemType,
} from '@modeso/types__aumico-lib-annualreport-be';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { IFinancialLineItemExtendedDTO } from '@modeso/types__aumico-lib-settings-be';
import { IGroupEntityDTO } from '@modeso/types__aumico-lib-templates-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextService } from 'angular-i18next';
import Debug from 'debug';
import { Observable, filter, first } from 'rxjs';
import { OkCancelDialogActions } from '../../../../../aumico-lib-core-fe/src/lib/enums/ok-cancel-actions.enum';
import { AutomaticTransferOfAnnualResultService } from '../../../public-api';
import { AccountMappingTemplateStatus } from '../../enums/account-mapping-template-status.enum';
import { EngineModes } from '../../enums/engineModes.enum';
import {
    AccountMappingFormHelper,
    candidateItemCaptions,
    fallbackPlanFor,
    translateWithValues,
    AccountMappingResult,
    MappingWarning,
    MappingWarningKind,
} from '../../helpers/accountMappingFormHelper';
import { IAccountMappingDataMapped } from '../../interfaces/IAccountMappingDataMapped.interface';
import { IAccountMappingFormInput } from '../../interfaces/IAccountMappingFormInput.interface';
import { Item } from '../../interfaces/menu-item.class';
import { TranslationResponse } from '../../interfaces/templates-translation-response-dto.interface';
import { TemplatesTranslationResponse } from '../../interfaces/templates-translation-response.interface';
import { IUncoveredShortfallSpecifications } from '../../interfaces/uncovered-shortfall-specifications.interface';
import { IUpdateAccount } from '../../interfaces/update-account.interface';
import { AccountMappingEngine } from '../../mappers/account-mapping-engin';
import { TemplatesProvider } from '../../provider/templates.provider';
import { isFli, isGroup, isSubTotal, isSubfli } from '../../type-guards/type-guards';
import { AccountMappingData } from '../../types/AccountMappingData.type';
const debug = Debug('aumico:aumico-lib-templates-fe:AccountMappingComponent');

const UNCOVERED_SHORTFALL_ERROR_TITLE_LOCALIZATION_KEY =
    'aumico_templates_mapping_engine_equity_group_not_defined_title';
const UNCOVERED_SHORTFALL_ERROR_DESCRIPTION_LOCALIZATION_KEY =
    'aumico_templates_mapping_engine_equity_group_not_defined_description';
@UntilDestroy()
@Component({
    selector: 'templates-account-mapping-form',
    templateUrl: 'account-mapping-form.component.html',
    styleUrls: ['account-mapping-form.component.scss'],
})
export class AccountMappingComponent implements OnInit, AfterViewInit, OnDestroy {
    balanceEngine: AccountMappingEngine | undefined;
    incomeEngine: AccountMappingEngine | undefined;
    transferredProfitAccountName: string | undefined;
    transferredSurplusAccountName: string | undefined;
    showUncoveredShortFallButton = false;
    balanceFinancialLineItems: IFinancialLineItemExtendedDTO[] = [];
    incomeFinancialLineItems: IFinancialLineItemExtendedDTO[] = [];
    // REV-18627 (C1b, B7): Warn-Oberfläche des Automappings — Bilanz- und GuV-Ergebnis
    // zusammengeführt (Warnungen dedupliziert nach kind+key, unmapped vereinigt).
    mappingWarnings: MappingWarning[] = [];
    /**
     * REV-22122: the user closed the notice. It stays closed until the next automatic mapping, which
     * brings a fresh result. The unmapped accounts stay visible in the account list either way — the
     * notice explains them, it is not the only place they appear.
     */
    mappingWarningsDismissed = false;
    /**
     * REV-22122: for a `NO_CANDIDATE` warning, the items the file names for that account (captions,
     * for the notice only — see `candidateItemCaptions`; the warning itself stays key-only, N4).
     */
    mappingWarningCandidates: Record<string, string[]> = {};

    get showMappingWarnings(): boolean {
        return (
            !this.mappingWarningsDismissed && (this.hasFormatMismatchWarning || this.otherMappingWarnings.length > 0)
        );
    }

    dismissMappingWarnings(): void {
        this.mappingWarningsDismissed = true;
    }

    /**
     * REV-22122 (SRR F1): the text of one notice entry. Key and item captions come from the file and are
     * spliced in after i18next (`translateWithValues`), never interpolated by it.
     */
    mappingWarningText(warning: MappingWarning): string {
        const candidates = this.mappingWarningCandidates[warning.key] ?? [];
        const kind =
            warning.kind === 'NO_CANDIDATE' && candidates.length > 1 ? 'AMBIGUOUS_CANDIDATES' : warning.kind;
        const translationKey = AccountMappingComponent.WARNING_TEXT_KEYS[kind];
        if (translationKey == null) return warning.key;
        return translateWithValues(
            (key, options) => this.localizedTexts.t(key, options) as string,
            translationKey,
            { key: warning.key, candidates: candidates.join(' / ') },
        );
    }

    private static readonly WARNING_TEXT_KEYS: Record<string, string> = {
        UNKNOWN_PURPOSE: 'aumico_templates_account_mapping_form_datev_unknown_purpose_warning',
        PURPOSE_SHAPED_VALUE_IN_NUMBER_PATH: 'aumico_templates_account_mapping_form_datev_purpose_shaped_value_warning',
        NO_CANDIDATE: 'aumico_templates_account_mapping_form_datev_no_candidate_warning',
        AMBIGUOUS_CANDIDATES: 'aumico_templates_account_mapping_form_datev_ambiguous_candidates_warning',
        UNKNOWN_FORMAT: 'aumico_templates_account_mapping_form_datev_unknown_format_warning',
    };

    get hasFormatMismatchWarning(): boolean {
        return this.mappingWarnings.some((warning) => warning.kind === 'FORMAT_MISMATCH');
    }

    get otherMappingWarnings(): MappingWarning[] {
        const warnings = this.mappingWarnings.filter((warning) => warning.kind !== 'FORMAT_MISMATCH');
        return AccountMappingComponent.capForDisplay(warnings);
    }

    // REV-18627(sec-fix2, F4/N4): die Mengenbegrenzung "höchstens 50 Einträge je Art + '… und N
    // weitere'" gehört an die Anzeige, nicht ins Ergebnisobjekt des Helpers — das Ergebnisobjekt
    // ist eine Datenstruktur für Aufrufer (geprüft von C1(p3)), kein Anzeigetext.
    // REV-18627(sec-fix3): gilt nur für die Warnungsliste (Meldung), nicht für die Liste der
    // unzugeordneten Konten (Arbeitsliste, muss vollständig sein — sonst wird ein Konto nie gefunden;
    // seit REV-22122 ist das die Kontenliste links vom Baum, `data-grid`, ungekürzt).
    private static readonly MAX_DISPLAYED_ITEMS_PER_KIND = 50;

    private static capForDisplay(warnings: MappingWarning[]): MappingWarning[] {
        const byKind = new Map<MappingWarningKind, MappingWarning[]>();
        warnings.forEach((warning) => {
            const list = byKind.get(warning.kind) ?? [];
            list.push(warning);
            byKind.set(warning.kind, list);
        });
        const capped: MappingWarning[] = [];
        byKind.forEach((list, kind) => {
            if (list.length <= AccountMappingComponent.MAX_DISPLAYED_ITEMS_PER_KIND) {
                capped.push(...list);
                return;
            }
            const overflow = list.length - AccountMappingComponent.MAX_DISPLAYED_ITEMS_PER_KIND;
            capped.push(
                ...list.slice(0, AccountMappingComponent.MAX_DISPLAYED_ITEMS_PER_KIND),
                { kind, key: `… und ${overflow} weitere` },
            );
        });
        return capped;
    }
    private balanceEngineMode: EngineModes | undefined;
    private incomeEngineMode: EngineModes | undefined;

    @ViewChild('settingsContent') settingsContent!: ElementRef;
    private resizeObserver: ResizeObserver | undefined;
    private intersectionObserver: IntersectionObserver | undefined;

    @Input() languages: string[] = ['de-ch'];
    @Input() enforceOverwrite: boolean;

    @Input() businessModel = AnnualReportBusinessModel.OR;

    languageUsed: string | undefined;
    globalGroups: IGroupEntityDTO[];

    specialFunctionsForm: FormGroup;
    _selectedForTransferProfit: string;
    _selectedForAnnualSurplus: string;
    mappendBalanceFlis: INameId[] = [];
    formInitialized = false;
    @Input() set language(value: string | undefined) {
        if (value != null && this._accountMappingInput != null) {
            this.languageUsed = value;
            this.determineVisibilityOfUncoveredShortfallButton();
            if (this.formInitialized) {
                this.updateChangedComponents(null, this._accountMappingInput);
            }
        }
    }
    @Input() defaultLanguage = 'de-ch';
    @Input() reportLocale: string;
    @Input() currency = '';

    @Input() isPreviousDataTakenFromPreviousBalanceAnnualReport: boolean;
    @Input() isPreviousDataTakenFromPreviousIncomeAnnualReport: boolean;

    previousAnnualProfitForMissingIncomePeriod: number | undefined;
    @Input() set previousAnnualProfit(value: number | undefined) {
        this.previousAnnualProfitForMissingIncomePeriod = value;
        this.automaticTransferOfAnnualResult();
    }

    automaticTransferToggleOn = false;
    @Input() set isAutomaticTransferOfAnnualResultEnabled(value: boolean) {

        if (value === true) {
            this.automaticTransferToggleOn = value;

            if (this.specialFunctionsForm) {
                this.specialFunctionsForm.controls['automaticTransferToggleOn'].setValue(value, { emitEvent: false });
            }
            this.automaticTransferOfAnnualResult();
        }
    }


    @Input() set applyAutomaticTransferOfAnnualResultForCurrentAndPreviousValues(value: boolean) {

        if (value) {
            this.automaticTransferToggleOn = value;

            if (this.specialFunctionsForm) {
                this.specialFunctionsForm.controls['automaticTransferToggleOn'].setValue(value, { emitEvent: false });
            }

            this.automaticTransferOfAnnualResult();
        }
    }

    @Input() shouldUsePreviousAnnualProfitFromCurrentIncomeStatement: boolean;
    errorInProfitFli = false;
    @Input() supportOperation = false;

    @Input() profitOfTheYearFliName: string;
    private importedProfitOfTheYearFliName: string;
    @Input() annualSurplusFliName: string | undefined;
    importedAnnualSurplusFliName: string | undefined;
    @Input() uncoveredShortfallAssetsFliName: string | undefined;
    @Input() uncoveredShortfallLiabilitiesFliName: string | undefined;
    _selectedUncoveredShortfallAssetsFli: string | undefined;
    _selectedUncoveredShortfallLiabilitiesFli: string | undefined;
    @Input() previousYearClosingIncomeFromAutofillReport: number | undefined;
    @Input() hasPreviousData: boolean;
    @Input() previousYearBalanceTemplate?: IAccountMappingTemplate;
    @Input() previousYearIncomeTemplate?: IAccountMappingTemplate;
    /**
     * REV-22122: the taxonomy the PREVIOUS year was imported with (`importedTaxonomy` of the
     * autofill source; only a DATEV one is used, see `fallbackPlanFor`). A follow-up year that
     * uploads only accounts has no taxonomy of its own, so an account new this year had no
     * assignment to follow and fell back to the SME plan. Its
     * assignments now reach the automatic mapping for exactly those accounts — never replacing a
     * carried-over mapping (see `_getWorkingEngineFromData`).
     */
    @Input() previousYearImportedTaxonomy?: {
        rawBalanceTemplate?: IAccountMappingTemplate;
        rawIncomeTemplate?: IAccountMappingTemplate;
    };
    @Input() copiedFinStatements?: IFinStatements[];
    private _accountMappingInput: IAccountMappingFormInput;
    public get accountMappingInput(): IAccountMappingFormInput {
        return this._accountMappingInput;
    }
    @Input()
    public set accountMappingInput(value: IAccountMappingFormInput) {
        this._accountMappingInput = value;

        if (this.reportLocale == null) {
            return;
        }
        this.determineVisibilityOfUncoveredShortfallButton();
        if (this.formInitialized) {
            this.updateChangedComponents(this._accountMappingInput, value);
        }
    }

    @Input() cashAndCashFli: string | undefined;

    @Input() isCashFlowTypeForFliUsed: boolean | undefined;

    @Input() allFinancialLineItems: IFinancialLineItemExtendedDTO[] = [];

    @Input() isSavedMode: boolean = false;

    @Output() incomeChangedEventEmitter = new EventEmitter<IAccountMappingDataMapped>();

    @Output() balanceChangedEventEmitter = new EventEmitter<IAccountMappingDataMapped>();
    @Output() addOrUpdateAccountInActiveReport = new EventEmitter<IUpdateAccount>();

    @Output() fliOrGroupDeleted: EventEmitter<IAccountMappingTemplateGroup | IAccountMappingTemplateGroupItemFLI> =
        new EventEmitter<IAccountMappingTemplateGroup | IAccountMappingTemplateGroupItemFLI>();
    @Output() translateTemplate: EventEmitter<void> = new EventEmitter<void>();
    @Output() specialFliChange: EventEmitter<Record<string, string>> = new EventEmitter<Record<string, string>>();
    @Output() automaticTransferOfAnnualResultEnabledChange: EventEmitter<boolean> = new EventEmitter<boolean>();

    @Output() errorInProfitFliDetected: EventEmitter<boolean> = new EventEmitter<boolean>();

    @Input() templatesTranslations$: Observable<TemplatesTranslationResponse>;
    private uncoveredShortfallAssetsFliCaption: string | undefined;
    private uncoveredShortfallEquityFliCaption: string | undefined;
    private uncoveredShortfallAssetsAccountName: string | undefined;
    private uncoveredShortfallEquityAccountName: string | undefined;

    constructor(
        private readonly localizedTexts: I18NextService,
        private readonly templatesProvider: TemplatesProvider,
        private readonly coreProvider: AumicoCoreProvider,
        private readonly automaticTransferOfAnnualResultService: AutomaticTransferOfAnnualResultService,
        private dialog: MatDialog,
    ) { }

    ngOnInit(): void {
        this.initHelperFunctions();
        this.templatesProvider
            .getGroups$()
            .pipe(untilDestroyed(this))
            .subscribe((groups) => {
                this.globalGroups = groups;
            });
        this.applyLocalizations();
        this.reportLocale = this.reportLocale.toLowerCase();
        this.determineVisibilityOfUncoveredShortfallButton();
        this.updateChangedComponents(null, this._accountMappingInput);

        this.templatesTranslations$.pipe(untilDestroyed(this)).subscribe((translation) => {
            const translatableItems = this.getTemplateTranslations(translation);
            this.fixNames([this.balanceEngine.getTemplateMapping().group], translatableItems);
            this.fixNames([this.incomeEngine.getTemplateMapping().group], translatableItems);
        });
        this.formInitialized = true;
    }

    ngAfterViewInit(): void {
        this.resizeObserver = new ResizeObserver(() => this.updateSettingsColumnHeight());
        this.resizeObserver.observe(this.settingsContent.nativeElement);
        this.intersectionObserver = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) {
                    this.updateSettingsColumnHeight();
                }
            },
            { threshold: 0 },
        );
        this.intersectionObserver.observe(this.settingsContent.nativeElement);
    }

    ngOnDestroy(): void {
        this.resizeObserver?.disconnect();
        this.intersectionObserver?.disconnect();
    }

    @HostListener('window:resize')
    onResize(): void {
        this.updateSettingsColumnHeight();
    }

    private updateSettingsColumnHeight(): void {
        const el = this.settingsContent.nativeElement as HTMLElement;
        const top = el.getBoundingClientRect().top;
        if (top <= 0) return;
        const height = window.innerHeight - top - 64;
        el.style.setProperty('--column-height', `${height}px`);
    }

    private initHelperFunctions(): void {
        this.specialFunctionsForm = new FormGroup({
            selectedForTransferProfit: new FormControl(),
            selectedForAnnualSurplus: new FormControl(),
            automaticTransferToggleOn: new FormControl(this.automaticTransferToggleOn),
            selectedUncoveredShortfallAssetsFli: new FormControl(),
            selectedUncoveredShortfallLiabilitiesFli: new FormControl(),
        });

        // Subscribe to changes in the 'selectedForTransferProfit' form control
        this.specialFunctionsForm.controls['selectedForTransferProfit'].valueChanges
            .pipe(untilDestroyed(this))
            .subscribe((newValue) => {
                this.onSelectedForTransferProfitChange(newValue);
            });
        this.specialFunctionsForm.controls['selectedForAnnualSurplus'].valueChanges
            .pipe(untilDestroyed(this))
            .subscribe((newValue) => {
                this.onSelectedForAnnualSurplusChange(newValue);
            });
        this.specialFunctionsForm.controls['selectedUncoveredShortfallAssetsFli'].valueChanges
            .pipe(untilDestroyed(this))
            .subscribe((newValue) => {
                this.onUncoveredShortfallAssetsFliChange(newValue);
            });
        this.specialFunctionsForm.controls['selectedUncoveredShortfallLiabilitiesFli'].valueChanges
            .pipe(untilDestroyed(this))
            .subscribe((newValue) => {
                this.onUncoveredShortfallLiabilitiesFliChange(newValue);
            });
    }

    private getTemplateTranslations(templateTranslations: TemplatesTranslationResponse): Map<string, Translation> {
        const translatableItems = new Map<string, Translation>();
        const flattenGroups: TranslationResponse[] = [
            ...templateTranslations.balanceGroups,
            ...templateTranslations.incomeGroups,
        ];
        for (const item of flattenGroups) {
            translatableItems.set(`${item.id}_${ItemType.GROUP}`, item.translations);
            translatableItems.set(`${item.id}_${ItemType.GROUP}_total`, item.totalTranslations);
        }

        const flattenFlis: TranslationResponse[] = [
            ...templateTranslations.balanceFlis,
            ...templateTranslations.incomeFlis,
        ];

        for (const item of flattenFlis) {
            translatableItems.set(`${item.id}_${ItemType.FLI}`, item.translations);
        }

        const flattenSubTotal: TranslationResponse[] = [
            ...templateTranslations.balanceSubtotals,
            ...templateTranslations.incomeSubtotals,
        ];

        for (const item of flattenSubTotal) {
            translatableItems.set(`${item.id}_${ItemType.SUB_TOTAL}`, item.translations);
        }

        const flattenSubfli: TranslationResponse[] = [
            ...templateTranslations.balanceSubflis,
            ...templateTranslations.incomeSubflis,
        ];

        for (const item of flattenSubfli) {
            translatableItems.set(`${item.id}_${ItemType.SUB_FLI}`, item.translations);
        }

        const flattenOfThatItem: TranslationResponse[] = [
            ...templateTranslations.balanceOfThatItems,
            ...templateTranslations.incomeOfThatItems,
        ];

        for (const item of flattenOfThatItem) {
            translatableItems.set(`${item.id}_${ItemType.OF_THAT_ITEM}`, item.translations);
        }

        return translatableItems;
    }
    private applyLocalizations(): void {
        this.transferredProfitAccountName = this.localizedTexts.t(
            'aumico_templates_account_mapping_form_transferred_profit_account_name',
            { lng: this.reportLocale },
        ) as string;
        this.transferredSurplusAccountName = this.localizedTexts.t(
            'aumico_templates_account_mapping_form_transferred_surplus_account_name',
            { lng: this.reportLocale },
        ) as string;

        this.uncoveredShortfallAssetsFliCaption = this.localizedTexts.t(
            'aumico_templates_account_mapping_form_uncovered_shortfall_assets_fli_caption',
            { lng: this.reportLocale },
        ) as string;
        this.uncoveredShortfallEquityFliCaption = this.localizedTexts.t(
            'aumico_templates_account_mapping_form_uncovered_shortfall_equity_fli_caption',
            { lng: this.reportLocale },
        ) as string;
        this.uncoveredShortfallAssetsAccountName = this.localizedTexts.t(
            'aumico_templates_account_mapping_form_uncovered_shortfall_assets_account_name',
            { lng: this.reportLocale },
        ) as string;
        this.uncoveredShortfallEquityAccountName = this.localizedTexts.t(
            'aumico_templates_account_mapping_form_uncovered_shortfall_equity_account_name',
            { lng: this.reportLocale },
        ) as string;
    }

    private updateChangedComponents(oldInput: IAccountMappingFormInput, newInput: IAccountMappingFormInput): void {
        if (newInput != null && (newInput.balance == null || newInput.income == null)) {
            return;
        }

        const rulesChanged = !this._oneLevelEqual((oldInput && oldInput.sheetSystem) || {}, newInput.sheetSystem);
        if (
            oldInput == null ||
            oldInput.balance !== newInput.balance ||
            oldInput.balance.template !== newInput.balance.template ||
            rulesChanged
        ) {
            if (this.enforceOverwrite) {
                newInput.balance.status = AccountMappingTemplateStatus.NEW;
            }
            this.balanceEngineMode = this.getEngineModeBasedOnMappingStatus(newInput.balance.status);
            //Define Uncovershortfall Specs
            let uncoveredShortfallAssetsFli;
            let uncoveredShortfallLiabilitiesFli;
            if (newInput.applyUncoveredShortfall) {
                //use values from imported taxonomy first, then other data
                if (
                    newInput.importedTaxonomyDefinitions &&
                    newInput.importedTaxonomyDefinitions.rawBalanceTemplate.uncoveredShortfallAssetsFli &&
                    newInput.importedTaxonomyDefinitions.rawBalanceTemplate.uncoveredShortfallLiabilitiesFli
                ) {
                    uncoveredShortfallAssetsFli =
                        newInput.importedTaxonomyDefinitions.rawBalanceTemplate.uncoveredShortfallAssetsFli;
                    uncoveredShortfallLiabilitiesFli =
                        newInput.importedTaxonomyDefinitions.rawBalanceTemplate.uncoveredShortfallLiabilitiesFli;
                } else {
                    uncoveredShortfallAssetsFli = newInput.uncoveredShortfallAssetsFli;
                    uncoveredShortfallLiabilitiesFli = newInput.uncoveredShortfallLiabilitiesFli;
                }
            }

            if ((newInput.balance.template as IAccountMappingTemplate).annualSurplusFli != null) {
                this.importedAnnualSurplusFliName = (
                    newInput.balance.template as IAccountMappingTemplate
                ).annualSurplusFli;
            }

            if (newInput.balance.template.profitOfTheYear != null) {
                this.profitOfTheYearFliName = newInput.balance.template.profitOfTheYear;
                this.importedProfitOfTheYearFliName = newInput.balance.template.profitOfTheYear;
            }

            const balanceTemplate = newInput.balance.template as IAccountMappingTemplate;
            if (balanceTemplate.uncoveredShortfallAssetsFli != null) {
                this.uncoveredShortfallAssetsFliName = balanceTemplate.uncoveredShortfallAssetsFli;
            }
            if (balanceTemplate.uncoveredShortfallLiabilitiesFli != null) {
                this.uncoveredShortfallLiabilitiesFliName = balanceTemplate.uncoveredShortfallLiabilitiesFli;
            }
            const copiedFinStatement = this.copiedFinStatements ? this.copiedFinStatements[0] : undefined;

            // we simply need to filter out undefined elements in the accounts array
            const filteredBalanceAccounts = newInput.balance.accounts.filter((acc) => acc !== null && acc !== undefined);
            newInput.balance.accounts = filteredBalanceAccounts;


            this.balanceEngine = this._getWorkingEngineFromData(
                newInput.balance,
                newInput.sheetSystem,
                this.reportLocale,
                {
                    startDate: newInput.dates.balanceStartDate,
                    endDate: newInput.dates.balanceEndDate,
                },
                this.balanceEngineMode,
                newInput.previousYearBalanceDuplicateAccountNumber,
                {
                    applyUncoveredShortfall: newInput.applyUncoveredShortfall,
                    uncoveredShortfallAssetsFli: uncoveredShortfallAssetsFli,
                    uncoveredShortfallLiabilitiesFli: uncoveredShortfallLiabilitiesFli,
                    uncoveredShortfallAssetsFliCaption: this.uncoveredShortfallAssetsFliCaption,
                    uncoveredShortfallEquityFliCaption: this.uncoveredShortfallEquityFliCaption,
                    uncoveredShortfallAssetsAccountName: this.uncoveredShortfallAssetsAccountName,
                    uncoveredShortfallEquityAccountName: this.uncoveredShortfallEquityAccountName,
                },
                newInput.importedTaxonomyDefinitions?.rawBalanceTemplate,
                this.previousYearBalanceTemplate,
                this.hasPreviousData,
                copiedFinStatement,
                this.previousYearImportedTaxonomy?.rawBalanceTemplate,
            );
        }

        // REV-22122: the imported taxonomy's special items apply only when the balance tree actually
        // holds them. When the user CANCELS the taxonomy dialog, the previous year's tree stays — and
        // the new file's item names (index-prefixed, "bp_0000xx…", different per file) are not in it:
        // taking them over left the report without an annual-result item ("Das Jahresergebnis-Konto
        // konnte nicht gefunden werden"). The template's own value stands in that case.
        const importedProfitOfTheYear = newInput.importedTaxonomyDefinitions?.rawBalanceTemplate?.profitOfTheYear;
        if (newInput.importedTaxonomyDefinitions?.rawBalanceTemplate && this.isBalanceItem(importedProfitOfTheYear)) {
            this.profitOfTheYearFliName = importedProfitOfTheYear;
            this.importedProfitOfTheYearFliName = importedProfitOfTheYear;
        }

        if (
            newInput.importedTaxonomyDefinitions?.rawBalanceTemplate.annualSurplusFli &&
            this.isBalanceItem(newInput.importedTaxonomyDefinitions.rawBalanceTemplate.annualSurplusFli)
        ) {
            this.annualSurplusFliName = newInput.importedTaxonomyDefinitions?.rawBalanceTemplate.annualSurplusFli;
            this.importedAnnualSurplusFliName =
                newInput.importedTaxonomyDefinitions?.rawBalanceTemplate.annualSurplusFli;
        }

        this.determineFliForTransferProfit();
        this.determineFliForAnnualSurplus();
        this.determineUncoveredShortfallFlis();

        if (this.balanceEngineMode == null) {
            throw new Error('There is no mode for the balance engine');
        }

        if (
            oldInput == null ||
            oldInput.income !== newInput.income ||
            oldInput.income.template !== newInput.income.template ||
            rulesChanged
        ) {
            if (this.enforceOverwrite) {
                newInput.income.status = AccountMappingTemplateStatus.NEW;
            }
            this.incomeEngineMode = this.getEngineModeBasedOnMappingStatus(newInput.income.status);
            const copiedFinStatement = this.copiedFinStatements ? this.copiedFinStatements[0] : undefined;

            // we simply need to filter out undefined elements in the accounts array
            const filteredIncomeAccounts = newInput.income.accounts.filter((acc) => acc !== null && acc !== undefined);
            newInput.income.accounts = filteredIncomeAccounts;

            this.incomeEngine = this._getWorkingEngineFromData(
                newInput.income,
                newInput.sheetSystem,
                this.reportLocale,

                {
                    startDate: newInput.dates.incomeStartDate,
                    endDate: newInput.dates.incomeEndDate,
                },
                this.incomeEngineMode,
                newInput.previousYearIncomeDuplicateAccountNumber,
                undefined,
                newInput.importedTaxonomyDefinitions?.rawIncomeTemplate,
                this.previousYearIncomeTemplate,
                this.hasPreviousData,
                copiedFinStatement,
                this.previousYearImportedTaxonomy?.rawIncomeTemplate,
            );
        }

        if (this.incomeEngineMode == null) {
            throw new Error('There is no mode for the income engine');
        }

        if (this.balanceEngine != null && this.incomeEngine != null) {
            const allAccounts: IAccount[] = [
                ...this.balanceEngine.getAllAccounts(),
                ...this.incomeEngine.getAllAccounts(),
            ];
            this.templatesProvider.setAllAccountsFromExcel(allAccounts);
        }
    }

    private _oneLevelEqual(obj1: object, obj2: object): boolean {
        const colobj = { ...obj1, ...obj2 };
        let equal = true;
        for (const key of Object.keys(colobj)) {
            equal = equal && obj1[key] === obj2[key];
            if (!equal) {
                break;
            }
        }
        return equal;
    }

    private getEngineModeBasedOnMappingStatus(status: AccountMappingTemplateStatus): EngineModes {
        if (status === AccountMappingTemplateStatus.AUTOFILLED) {
            return EngineModes.AUTOFILL;
        } else if (status === AccountMappingTemplateStatus.PREVIOUS_AUTOMATIC_FILL) {
            return EngineModes.PREVIOUS_AUTOMATIC_FILL;
        } else if (status === AccountMappingTemplateStatus.COPY_FROM_OTHER_REPORT) {
            return EngineModes.COPY_FROM_OTHER_REPORT;
        } else {
            return EngineModes.MANUAL;
        }
    }

    /** REV-22122: true when the working balance tree has an item of this name. */
    private isBalanceItem(name: string | undefined): boolean {
        if (name == null || this.balanceEngine == null) return false;
        const mappedTemplate = this.balanceEngine.getMappedTemplate();
        return this.balanceEngine.getFlisAndSubFlisOfGroups(mappedTemplate.group).some((item) => item.name === name);
    }

    private _getWorkingEngineFromData(
        data: AccountMappingData,
        sheetSystem: IFinancialStatementInterpretation,
        language: string,
        dates: { startDate: string; endDate: string },
        engineMode: EngineModes,
        previousYearDuplicateAccountNumber?: Set<string>,
        uncoveredShortfallSpecifications?: IUncoveredShortfallSpecifications,
        importedTaxonomy?: IAccountMappingTemplate,
        previousYearTemplate?: IAccountMappingTemplate,
        hasPreviousData?: boolean,
        copiedFinStatement?: IFinStatements,
        previousYearImportedTaxonomy?: IAccountMappingTemplate,
    ): AccountMappingEngine {

        if (data.status === AccountMappingTemplateStatus.NEW) {
            // for new empty mapping

            return AccountMappingEngine.getMappingEngine(
                data.accounts,
                data.template,
                data.balanceSwitchAccounts != null ? data.balanceSwitchAccounts : [],
                sheetSystem,
                dates,
                engineMode,
                language,
                uncoveredShortfallSpecifications,
                importedTaxonomy,
                previousYearTemplate,
            );
        } else {
            // for saved, autofill or taking from previous
            const deletePreviousYearData = engineMode === EngineModes.COPY_FROM_OTHER_REPORT && !hasPreviousData;
            // REV-18627(C1d/R7): the NEW branch above passes the imported plan straight
            // through as templateMapping. This branch keeps the previous year's template
            // (the previous mapping lives in it, B4) but the plan itself must still reach
            // the engine, so it is mirrored onto the used template rather than swapping it.
            // REV-18627(sec F10): only once the user confirmed the taxonomy-replace dialog
            // (same signal as replaceExistingMapping below), and onto a copy — data.template
            // belongs to the caller's input object, not to this component.
            let template = data.template;
            if (importedTaxonomy != null && data.template != null && this.enforceOverwrite) {
                template = {
                    ...data.template,
                    accountAssignments: importedTaxonomy['accountAssignments'],
                    accountAssignmentsFormat: importedTaxonomy['accountAssignmentsFormat'],
                    // REV-22122: the DATEV side per item travels with the plan (see the engine's
                    // `getImportedItemSides`).
                    accountItemSides: importedTaxonomy['accountItemSides'],
                } as typeof data.template;
            }
            const engine = new AccountMappingEngine(
                data.accounts,
                template,
                data.balanceSwitchAccounts != null ? data.balanceSwitchAccounts : [],
                sheetSystem,
                dates,
                engineMode,
                previousYearDuplicateAccountNumber,
                uncoveredShortfallSpecifications,
                previousYearTemplate,
                deletePreviousYearData,
                copiedFinStatement,
            );
            // REV-22122: no confirmed taxonomy this year — the file brought none, or the user
            // cancelled the replace dialog (then the delivered one stays unused, sec F10). The
            // previous year's plan places the accounts that are new this year. It is handed to the
            // engine as a FALLBACK, not written into the template: the template stays exactly the
            // saved one (a changed template reads as unsaved changes after a reload), and the engine
            // uses the fallback only while the template carries no plan of its own.
            // `enforceOverwrite` is false here, so the cascade runs with `replaceExistingMapping:
            // false` and only maps accounts nothing holds yet — the carried-over mapping stays (AC 6).
            // Only a DATEV plan qualifies (review R1, see `fallbackPlanFor`).
            engine.setFallbackImportedPlan(
                fallbackPlanFor(previousYearImportedTaxonomy, data.accounts, this.enforceOverwrite),
            );
            return engine;
        }
    }

    balanceChanged(event: IAccountMappingDataMapped): void {
        this.balanceChangedEventEmitter.emit(event);
    }

    incomeChanged(event: IAccountMappingDataMapped): void {
        this.automaticTransferOfAnnualResult();

        this.incomeChangedEventEmitter.emit(event);
    }

    private automaticTransferOfAnnualResult(): void {
        if (!this.automaticTransferToggleOn || this.balanceEngine == null || this.incomeEngine == null) {
            return;
        }

        const mainBalanceGroup = this.balanceEngine.getMappedTemplate().group;
        const currentAnnualProfitResult = this.incomeEngine.getTopGroup().calculatedClosingBalance;

        const profitFli = this.selectedForTransferProfit;
        this.balanceEngine.setProfitOfTheYearFliName(this.selectedForTransferProfit);

        let previousAnnualProfitResult = 0;

        if (this.shouldUsePreviousAnnualProfitFromCurrentIncomeStatement) {
            previousAnnualProfitResult = this.incomeEngine.getTopGroup().calculatedOpeningBalance;

        } else if (this.previousAnnualProfitForMissingIncomePeriod != null) {
            previousAnnualProfitResult = this.previousAnnualProfitForMissingIncomePeriod;
        }

        if (this.shouldUsePreviousAnnualProfitFromCurrentIncomeStatement || this.previousAnnualProfitForMissingIncomePeriod != null) {
            this.automaticTransferOfAnnualResultService.mapAnnualProfitToBalanceGroup(mainBalanceGroup, currentAnnualProfitResult, previousAnnualProfitResult, profitFli, false, this.reportLocale);

        } else if (!this.shouldUsePreviousAnnualProfitFromCurrentIncomeStatement && this.previousAnnualProfitForMissingIncomePeriod == null) {

            // If there is no data for previous annual profit, then we don't automatically change it
            this.automaticTransferOfAnnualResultService.mapAnnualProfitToBalanceGroup(mainBalanceGroup, currentAnnualProfitResult, previousAnnualProfitResult, profitFli, true, this.reportLocale);

        }

        this.balanceEngine.calculateAll();

    }

    // Method to handle changes in the 'selectedForTransferProfit' form control
    onSelectedForTransferProfitChange(newValue: INameId): void {
        if (newValue == null) {
            return;
        }

        if (newValue.id !== this._selectedForTransferProfit) {
            this.automaticTransferOfAnnualResultService.moveAnnualProfitAccountToAnotherFli(
                this.balanceEngine.getMappedTemplate().group,
                newValue.id,
            );
        }
        this.importedProfitOfTheYearFliName = newValue.id;
        this._selectedForTransferProfit = newValue.id;
        this.specialFliChange.emit({ profitOfTheYear: newValue.id });


        this.balanceEngine.calculateAll();
    }

    // Method to handle changes in the 'annualSurplusFliName' form control
    onSelectedForAnnualSurplusChange(newValue: INameId | undefined): void {
        if (newValue == null) {
            return;
        }
        this.importedAnnualSurplusFliName = newValue.id;
        this._selectedForAnnualSurplus = newValue.id;
        this.specialFliChange.emit({ annualSurplusFliName: newValue.id });
    }

    openResetFliDialog(): void {
        const dialogRef = this.dialog.open<ModalComponent, ModalData, OkCancelDialogActions>(ModalComponent, {
            disableClose: false,
            panelClass: 'custom-modalbox',
            data: {
                showCloseButton: true,
                titleIcon: ModalTitleIcons.INFO,
                footerButtons: [
                    {
                        localizationKey: 'aumico_annual_report_execute_button',
                        action: OkCancelDialogActions.OK,
                        buttonColor: 'primary',
                    },
                    {
                        localizationKey: 'aumico_annual_report_cancel_button',
                        action: OkCancelDialogActions.CANCEL,
                        buttonColor: 'basic',
                    },
                ],
                content: {
                    text: ['aumico_annual_report_mapping_reset_fli_dialog_content'],
                    icon: 'document-information',
                },
            },
        });

        dialogRef
            ?.afterClosed()
            .pipe(
                first(),
                filter((result) => result != null),
                untilDestroyed(this),
            )
            .subscribe((result) => {
                if (result === OkCancelDialogActions.OK) {
                    this.dispatchGetTranslations();
                }
            });
    }
    private dispatchGetTranslations(): void {
        this.translateTemplate.emit();
    }

    openLoadFliCfsSettingsDialog(): void {
        const dialogRef = this.dialog.open<ModalComponent, ModalData, OkCancelDialogActions>(ModalComponent, {
            disableClose: false,
            panelClass: 'custom-modalbox',
            data: {
                showCloseButton: true,
                titleIcon: ModalTitleIcons.INFO,
                footerButtons: [
                    {
                        localizationKey: 'aumico_annual_report_execute_button',
                        action: OkCancelDialogActions.OK,
                        buttonColor: 'primary',
                    },
                    {
                        localizationKey: 'aumico_annual_report_cancel_button',
                        action: OkCancelDialogActions.CANCEL,
                        buttonColor: 'basic',
                    },
                ],
                content: {
                    text: ['aumico_annual_report_mapping_load_fli_cfs_settings_dialog_content'],
                    icon: 'document-information',
                },
            },
        });

        dialogRef
            ?.afterClosed()
            .pipe(
                first(),
                filter((result) => result != null),
                untilDestroyed(this),
            )
            .subscribe((result) => {
                if (result === OkCancelDialogActions.OK) {
                    this.loadFliCfsFromSettings([this.balanceEngine.getTemplateMapping().group]);
                    this.loadFliCfsFromSettings([this.incomeEngine.getTemplateMapping().group]);
                }
            });
    }

    private loadFliCfsFromSettings(groups: IAccountMappingTemplateGroup[]): void {
        groups.forEach((group: IAccountMappingTemplateGroup) => {
            // Recursively add cashflow type
            this.loadFliCfsFromSettings(group.groups);

            group.items.forEach((item) => {
                if (isFli(item)) {
                    const matchingFli = this.allFinancialLineItems.find((fli) => fli.name === item.name);
                    if (matchingFli != null) {
                        item.cashFlowType = matchingFli.cashflow;
                    }
                }
            });
        });
    }

    // Function to list all group names and item names
    private fixNames(groups: IAccountMappingTemplateGroup[], translatableItems: Map<string, Translation>): void {
        const getKey = (item: Item, isGroupTotal?: boolean) => {
            if (isGroup(item)) {
                const key = `${item.name}_${ItemType.GROUP}`;
                return isGroupTotal ? `${key}_total` : key;
            } else {
                return `${item.name}_${item.type}`;
            }
        };
        groups.forEach((group: IAccountMappingTemplateGroup) => {
            const translationOfGroup = translatableItems.get(getKey(group));
            const translationOfGroupTotal = translatableItems.get(getKey(group, true));

            // REV-21863: guard on the VALUE, not just the entry. A renamed item deviates from the
            // template, so the response carries no text for it any more — keep what the mapping
            // already holds (the user's own wording) instead of blanking the caption.
            if (translationOfGroup?.[this.languageUsed] != null) {
                group.title = translationOfGroup[this.languageUsed];
            }
            if (translationOfGroupTotal?.[this.languageUsed] != null) {
                group.totalName = translationOfGroupTotal[this.languageUsed];
            }

            // Recursively add names from sub-groups
            this.fixNames(group.groups, translatableItems);

            // Add item names, assuming each item has a 'name' field
            group.items.forEach((item) => {
                const translation = translatableItems.get(getKey(item));
                if (isFli(item)) {
                    if (translation?.[this.languageUsed] != null) {
                        item.caption = translation[this.languageUsed];
                    }
                    item.accounts.forEach((child) => {
                        if (isSubfli(child)) {
                            const key = getKey(child);
                            const translations = translatableItems.get(key);
                            if (translations?.[this.languageUsed] != null) {
                                child.caption = translations[this.languageUsed];
                            }
                        }
                    });
                } else if (isSubTotal(item)) {
                    if (translation?.[this.languageUsed] != null) {
                        item.caption = translation[this.languageUsed];
                    }
                }
            });
        });
    }

    onSMEClick(): void {
        if (this.balanceEngineMode == null) {
            throw new Error('There is no mode for the balance engine');
        }

        if (this.balanceEngine == null) {
            throw new Error('There is no balance engine');
        }

        if (this.incomeEngineMode == null) {
            throw new Error('There is no mode for the income engine');
        }

        if (this.incomeEngine == null) {
            throw new Error('There is no income engine');
        }
        // REV-18627 (C1c, Tor-3-Befund): eine Postenstruktur wurde in DIESER Sitzung importiert
        // (importedTaxonomy wird nicht vererbt — B2 — darum ist dieses Feld selbst das Signal)
        // UND der Anwender hat das Übernehmen im Dialog bestätigt (enforceOverwrite). Bricht er
        // ab, bleibt enforceOverwrite false und die bestehende Zuordnung wird nur ergänzt.
        const replaceExistingMapping =
            this.accountMappingInput.importedTaxonomyDefinitions != null && this.enforceOverwrite;

        const balanceResult = this.mapToSMEAccountPlan(
            this.balanceEngine,
            this.balanceEngineMode,
            this.accountMappingInput.previousYearBalanceDuplicateAccountNumber,
            this.businessModel,
            replaceExistingMapping,
        );
        this.balanceEngine = balanceResult.engine;

        const incomeResult = this.mapToSMEAccountPlan(
            this.incomeEngine,
            this.incomeEngineMode,
            this.accountMappingInput.previousYearIncomeDuplicateAccountNumber,
            this.businessModel,
            replaceExistingMapping,
        );
        this.incomeEngine = incomeResult.engine;

        this.mappingWarnings = this.mergeMappingWarnings(balanceResult.result, incomeResult.result);
        // Only the entries the notice shows (at most 50 per kind, `capForDisplay`) — SRR F2.
        this.mappingWarningCandidates = this.describeNoCandidateWarnings(this.otherMappingWarnings, [
            this.balanceEngine,
            this.incomeEngine,
        ]);
        // REV-22122: a new result opens the notice again, even if the user closed the previous one.
        this.mappingWarningsDismissed = false;
        // The unmapped accounts are the account list next to the tree (`data-grid`, test id
        // `datev-unmapped-accounts`) — REV-18627 had built a second list for them here.
    }

    /**
     * REV-22122: the item captions each `NO_CANDIDATE` account points at, from the engine that holds
     * the account (balance or income) — its plan, format and tree. Display only.
     */
    private describeNoCandidateWarnings(
        warnings: MappingWarning[],
        engines: (AccountMappingEngine | undefined)[],
    ): Record<string, string[]> {
        const described: Record<string, string[]> = {};
        warnings
            .filter((warning) => warning.kind === 'NO_CANDIDATE')
            .forEach((warning) => {
                for (const engine of engines) {
                    const account = engine?.getAllAccounts().find((a) => a?.accountNumber === warning.key);
                    const plan = engine?.getImportedMapping;
                    if (engine == null || account == null || plan == null) continue;
                    const items = engine.getFlisAndSubFlisOfGroups(engine.getMappedTemplate().group);
                    described[warning.key] = candidateItemCaptions(
                        account,
                        plan,
                        engine.getImportedMappingFormat,
                        items,
                    );
                    return;
                }
            });
        return described;
    }

    /** REV-18627 (C1b, B7): dedupliziert nach kind+key — Bilanz und GuV können dieselbe Warnung tragen. */
    private mergeMappingWarnings(
        balanceResult: AccountMappingResult | undefined,
        incomeResult: AccountMappingResult | undefined,
    ): MappingWarning[] {
        const seen = new Set<string>();
        const merged: MappingWarning[] = [];
        for (const warning of [...(balanceResult?.warnings ?? []), ...(incomeResult?.warnings ?? [])]) {
            const dedupeKey = `${warning.kind}:${warning.key}`;
            if (!seen.has(dedupeKey)) {
                seen.add(dedupeKey);
                merged.push(warning);
            }
        }
        return merged;
    }

    determineFliForTransferProfit(): void {
        if (this._selectedForTransferProfit == null) {
            if (this.importedProfitOfTheYearFliName) {
                this._selectedForTransferProfit = this.importedProfitOfTheYearFliName;
            } else if (this.profitOfTheYearFliName) {
                this._selectedForTransferProfit = this.profitOfTheYearFliName;
            }
        }

        // Ensure balanceEngine and allFlis are defined
        let mappedFliOptions: INameId[] = [];
        if (this.balanceEngine && this.balanceEngine.allFlis) {
            mappedFliOptions = this.balanceEngine.allFlis.map((fli) => ({
                id: fli.name, // Use the appropriate unique identifier
                name: fli.caption,
            }));
        }
        this.mappendBalanceFlis = mappedFliOptions;
        if (this.specialFunctionsForm != null) {
            const value = this.mappendBalanceFlis.find((fli) => fli.id === this._selectedForTransferProfit);
            this.specialFunctionsForm.controls['selectedForTransferProfit'].setValue(value);
        }
    }

    determineFliForAnnualSurplus(): void {
        if (this._selectedForAnnualSurplus == null) {
            if (this.importedAnnualSurplusFliName) {
                this._selectedForAnnualSurplus = this.importedAnnualSurplusFliName;
            } else if (this.annualSurplusFliName) {
                this._selectedForAnnualSurplus = this.annualSurplusFliName;
            }
        }

        // Ensure balanceEngine and allFlis are defined
        let mappedFliOptions: INameId[] = [];
        if (this.balanceEngine && this.balanceEngine.allFlis) {
            mappedFliOptions = this.balanceEngine.allFlis.map((fli) => ({
                id: fli.name, // Use the appropriate unique identifier
                name: fli.caption,
            }));
        }
        this.mappendBalanceFlis = mappedFliOptions;

        if (this.specialFunctionsForm != null) {
            const value = this.mappendBalanceFlis.find((fli) => fli.id === this._selectedForAnnualSurplus);
            this.specialFunctionsForm.controls['selectedForAnnualSurplus'].setValue(value);
        }
    }

    get selectedForTransferProfit(): string | null {
        const selectedValue = this.specialFunctionsForm?.controls['selectedForTransferProfit']?.value as
            | INameId
            | null
            | undefined;

        if (selectedValue?.id != null) {
            return selectedValue.id;
        }

        return this._selectedForTransferProfit ?? this.importedProfitOfTheYearFliName ?? this.profitOfTheYearFliName ?? null;
    }
    determineUncoveredShortfallFlis(): void {
        if (!this.showUncoveredShortFallButton) {
            return;
        }

        if (this._selectedUncoveredShortfallAssetsFli == null && this.uncoveredShortfallAssetsFliName != null) {
            this._selectedUncoveredShortfallAssetsFli = this.uncoveredShortfallAssetsFliName;
        }
        if (this._selectedUncoveredShortfallLiabilitiesFli == null && this.uncoveredShortfallLiabilitiesFliName != null) {
            this._selectedUncoveredShortfallLiabilitiesFli = this.uncoveredShortfallLiabilitiesFliName;
        }

        let mappedFliOptions: INameId[] = [];
        if (this.balanceEngine && this.balanceEngine.allFlis) {
            mappedFliOptions = this.balanceEngine.allFlis.map((fli) => ({
                id: fli.name,
                name: fli.caption,
            }));
        }

        if (this.specialFunctionsForm != null) {
            const assetsValue = mappedFliOptions.find((fli) => fli.id === this._selectedUncoveredShortfallAssetsFli);
            this.specialFunctionsForm.controls['selectedUncoveredShortfallAssetsFli'].setValue(assetsValue ?? null, { emitEvent: false });

            const liabValue = mappedFliOptions.find((fli) => fli.id === this._selectedUncoveredShortfallLiabilitiesFli);
            this.specialFunctionsForm.controls['selectedUncoveredShortfallLiabilitiesFli'].setValue(liabValue ?? null, { emitEvent: false });
        }
    }

    onUncoveredShortfallAssetsFliChange(newValue: INameId | null): void {
        if (newValue == null) {
            return;
        }
        const currentLiab = this._selectedUncoveredShortfallLiabilitiesFli ?? null;
        this._selectedUncoveredShortfallAssetsFli = newValue.id;
        this.uncoveredShortfallAssetsFliName = newValue.id;
        this.balanceEngine.changeUncoveredShortfallFlis(newValue.id, currentLiab);
        this.specialFliChange.emit({ uncoveredShortfallAssetsFli: newValue.id });
    }

    onUncoveredShortfallLiabilitiesFliChange(newValue: INameId | null): void {
        if (newValue == null) {
            return;
        }
        const currentAssets = this._selectedUncoveredShortfallAssetsFli ?? null;
        this._selectedUncoveredShortfallLiabilitiesFli = newValue.id;
        this.uncoveredShortfallLiabilitiesFliName = newValue.id;
        this.balanceEngine.changeUncoveredShortfallFlis(currentAssets, newValue.id);
        this.specialFliChange.emit({ uncoveredShortfallLiabilitiesFli: newValue.id });
    }

    get selectedForAnnualSurplus(): string | null {
        const selectedValue = this.specialFunctionsForm?.controls['selectedForAnnualSurplus']?.value as
            | INameId
            | null
            | undefined;

        if (selectedValue?.id != null) {
            return selectedValue.id;
        }

        return this._selectedForAnnualSurplus ?? this.importedAnnualSurplusFliName ?? this.annualSurplusFliName ?? null;
    }

    onMapIncomeTotalToProfitOfTheYearClick(): void {
        const selectedProfitFli = this.selectedForTransferProfit;
        if (selectedProfitFli == null) {
            return;
        }

        this.balanceEngine.setProfitOfTheYearFliName(selectedProfitFli);
        if (this.transferredProfitAccountName != null) {
            this.balanceEngine.setSpecialAccountNameOfProfitFli(this.transferredProfitAccountName);
        } else {
            this.balanceEngine.setSpecialAccountNameOfProfitFli('Transferred profit');
        }

        this.mapIncomeTotal(false);
    }

    onMapIncomeTotalToAnnualSurplusClick(): void {
        if (this.balanceEngine == null) {
            throw new Error('There is no balance engine');
        }

        if (this.incomeEngine == null) {
            throw new Error('There is no income engine');
        }

        if (this.importedAnnualSurplusFliName == null && this.annualSurplusFliName == null) {
            throw new Error('annualSurplusFliName is undefined');
        }

        const selectedAnnualSurplusFli = this.selectedForAnnualSurplus;
        if (selectedAnnualSurplusFli == null) {
            return;
        }

        this.balanceEngine.setAnnualSurplusFliName(selectedAnnualSurplusFli);

        if (this.transferredSurplusAccountName != null) {
            this.balanceEngine.setSpecialAccountNameOfProfitFli(this.transferredSurplusAccountName);
        } else {
            this.balanceEngine.setSpecialAccountNameOfProfitFli('Transferred profit');
        }

        this.mapIncomeTotal(true);
    }

    private mapIncomeTotal(mapToAnnualSurplus: boolean): void {
        const mappedTemplateGroup = this.balanceEngine.getMappedTemplate().group;
        let incomeStatementClosingTotal = 0;
        const incomeTemplateClosingValue = this.incomeEngine.getTopGroup().calculatedClosingBalance;
        if (incomeTemplateClosingValue != null) {
            incomeStatementClosingTotal = incomeTemplateClosingValue;
        }

        let incomeStatementOpeningTotal = 0;
        const incomeTemplateOpeningValue = this.incomeEngine.getTopGroup().calculatedOpeningBalance;

        // CHANGED: isPreviousDataTakenFromPreviousIncomeAnnualReport Using ! instead of === false to catch null/undefined
        if (!this.isPreviousDataTakenFromPreviousIncomeAnnualReport && incomeTemplateOpeningValue != null) {
            incomeStatementOpeningTotal = incomeTemplateOpeningValue;
        } else if (this.isPreviousDataTakenFromPreviousIncomeAnnualReport && this.previousYearClosingIncomeFromAutofillReport != null) {
            incomeStatementOpeningTotal = this.previousYearClosingIncomeFromAutofillReport;
        } else if (incomeTemplateOpeningValue != null) {
            incomeStatementOpeningTotal = incomeTemplateOpeningValue;
        }

        this.balanceEngine.mapIncomeTotalToBalanceProfitFliOrAnnualSurplus(
            mappedTemplateGroup,
            incomeStatementClosingTotal,
            incomeStatementOpeningTotal,
            this.isPreviousDataTakenFromPreviousBalanceAnnualReport,
            mapToAnnualSurplus,
        );

        this.balanceEngine.recalculateUncoveredShortfall();
    }

    private mapToSMEAccountPlan(
        engine: AccountMappingEngine,
        engineMode: EngineModes,
        previousYearDuplicateAccountNumber: Set<string> | undefined,
        businessModel: AnnualReportBusinessModel,
        replaceExistingMapping: boolean,
    ): { engine: AccountMappingEngine; result: AccountMappingResult | undefined } {
        const mappedTemplate = engine.getMappedTemplate();
        const mappedTemplateGroup = mappedTemplate.group;
        const originallyMappedAccountNumbers: Set<string> =
            AccountMappingFormHelper.getMappedAccounts(mappedTemplateGroup);
        const accounts = engine.getAllAccounts();
        const balanceSwitchAccounts = engine.getBalanceSwitchAccounts();
        const sheetSystemRules = engine.getSheetSystemRules();
        const extras = engine.getDates();
        const uncoveredShortfallSpecifications = engine.getUncoveredShortfallSpecifications();
        const smeMappingItems = engine.getFlisAndSubFlisOfGroups(mappedTemplateGroup);
        const importedMapping = engine.getImportedMapping;
        const importedMappingFormat = engine.getImportedMappingFormat;
        const importedItemSides = engine.getImportedItemSides;
        const result: AccountMappingResult | undefined =
            AccountMappingFormHelper.mapSmeAccountsToFlisAndSubFlis(
                smeMappingItems,
                accounts,
                originallyMappedAccountNumbers,
                businessModel,
                importedMapping,
                importedMappingFormat,
                { replaceExistingMapping, itemSides: importedItemSides },
            ) || undefined;
        const newEngine =
            engineMode !== EngineModes.PREVIOUS_AUTOMATIC_FILL
                ? new AccountMappingEngine(
                      accounts,
                      mappedTemplate,
                      balanceSwitchAccounts,
                      sheetSystemRules,
                      extras,
                      engineMode,
                      undefined,
                      uncoveredShortfallSpecifications,
                  )
                : new AccountMappingEngine(
                      accounts,
                      mappedTemplate,
                      balanceSwitchAccounts,
                      sheetSystemRules,
                      extras,
                      engineMode,
                      previousYearDuplicateAccountNumber,
                      uncoveredShortfallSpecifications,
                  );
        // REV-22122: the previous year's fallback plan outlives the rebuilt engine (a second click).
        newEngine.setFallbackImportedPlan(engine.getFallbackImportedPlan);
        return { engine: newEngine, result };
    }

    onApplyUncoveredShortfallClick(): void {
        if (this.accountMappingInput.balance.template.equityGroupName == null) {
            this.coreProvider.dispatchAddNotification(
                UNCOVERED_SHORTFALL_ERROR_TITLE_LOCALIZATION_KEY,
                UNCOVERED_SHORTFALL_ERROR_DESCRIPTION_LOCALIZATION_KEY,
                NotificationType.ERROR,
            );

            return;
        }

        this.balanceEngine.applyUncoveredShortfall();
        this.balanceEngine.calculateAll();
    }

    onAddOrUpdateActiveReport(updatedAccountData: IUpdateAccount): void {
        this.addOrUpdateAccountInActiveReport.emit(updatedAccountData);
    }

    public onFliOrGroupDeleted(deletedItem: IAccountMappingTemplateGroup | IAccountMappingTemplateGroupItemFLI): void {
        this.fliOrGroupDeleted.emit(deletedItem);
    }

    private determineVisibilityOfUncoveredShortfallButton(): void {
        const options = this.accountMappingInput;

        this.showUncoveredShortFallButton =
            options.applyUncoveredShortfall != null ? options.applyUncoveredShortfall : false;
    }

    onAutomaticTransferToggleChange(enabled: boolean): void {
        // If the switch is turned off, we want to map the profit of the year back to the income statement manually
        if (enabled === false) {
            this.automaticTransferToggleOn = false;
            this.onMapIncomeTotalToProfitOfTheYearClick();
        }

        this.automaticTransferOfAnnualResultEnabledChange.emit(enabled);

    }
}
